import React, { useEffect, useState } from 'react';
import { Loader2, X, ShieldCheck } from 'lucide-react';
import { apiClient } from '../../api/client';
import type { components } from '../../api/types';

export type VerifyExtractionResult = components['schemas']['VerifyExtractionResult'];

export interface VerificationProgressProps {
  jobId: string;
  url: string;
  onComplete: (result: VerifyExtractionResult) => void;
  onError: (errorMessage: string) => void;
  onCancel: () => void;
}

const STEPS = [
  'Validating target URL against safety policy...',
  'Connecting to merchant webpage...',
  'Extracting current price, title, and currency...',
  'Verifying discount and product metadata...',
];

export const VerificationProgress: React.FC<VerificationProgressProps> = ({
  jobId,
  url,
  onComplete,
  onError,
  onCancel,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    // Step progression ticker for user feedback
    const stepInterval = setInterval(() => {
      setCurrentStepIndex((prev) => (prev < STEPS.length - 1 ? prev + 1 : prev));
    }, 2500);

    const timerInterval = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);

    return () => {
      clearInterval(stepInterval);
      clearInterval(timerInterval);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    let pollTimeout: any = null;

    const pollStatus = async () => {
      try {
        const { data, error } = await apiClient.GET('/api/v1/verify/{job_id}', {
          params: { path: { job_id: jobId } },
        });

        if (!isMounted) return;

        if (error) {
          onError(
            typeof error === 'object' && 'detail' in error
              ? String((error as any).detail)
              : 'Failed to verify URL status'
          );
          return;
        }

        if (data.status === 'COMPLETED') {
          if (data.result) {
            onComplete(data.result);
          } else {
            onError('Verification completed but no product data could be extracted.');
          }
          return;
        }

        if (data.status === 'FAILED') {
          const errDetail =
            data.error && typeof data.error === 'object' && 'detail' in data.error
              ? String(data.error['detail'])
              : 'Product extraction failed or page is blocked.';
          onError(errDetail);
          return;
        }

        // Still PENDING, poll again after 1.5s
        pollTimeout = setTimeout(pollStatus, 1500);
      } catch (err: any) {
        if (!isMounted) return;
        onError(err.message || 'Network error while checking verification.');
      }
    };

    pollStatus();

    return () => {
      isMounted = false;
      if (pollTimeout) clearTimeout(pollTimeout);
    };
  }, [jobId, onComplete, onError]);

  let domain = url;
  try {
    domain = new URL(url).hostname;
  } catch {
    // fallback
  }

  return (
    <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-900/60 shadow-xs space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
            Verifying Product Page
          </h3>
        </div>
        <button
          onClick={onCancel}
          className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-3 py-2">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600 dark:text-emerald-400 shrink-0" />
        <div className="text-xs">
          <p className="font-semibold text-slate-800 dark:text-slate-200">
            {STEPS[currentStepIndex]}
          </p>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5">
            Target store: <span className="font-mono">{domain}</span> ({elapsedSeconds}s elapsed)
          </p>
        </div>
      </div>

      <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
        <div
          className="bg-emerald-500 h-full transition-all duration-500 ease-out rounded-full"
          style={{ width: `${Math.min(100, (currentStepIndex + 1) * 25)}%` }}
        />
      </div>
    </div>
  );
};
